// Client-side mirror of server/ops/permissions.ts.
//
// Server enforces role access at every endpoint; this module drives the
// UX (which nav items render, which buttons appear). If the two ever
// disagree, the server wins — but they should stay in sync to avoid
// users seeing controls that immediately 403.
//
// FOUR distinct surfaces (no impersonation, no mode-switching):
//   1. Platform Admin   — ops_users.role='owner' (e.g. sandeep@gozupees.com)
//   2. Platform Staff   — ops_users.role='admin' (e.g. sandeep+cocomoon@…)
//   3. Tenant Admin     — ops_users.role='viewer' + tenant_users='admin'
//   4. Tenant User      — ops_users.role='viewer' + tenant_users='user'
//
// See server/ops/permissions.ts for the authoritative mapping + the
// reasoning behind each role's visibility.

export type PlatformRole = "owner" | "admin" | "viewer";
// Tenant-side roles after the white-glove model: just admin (Amber) and
// user (CSR like Sela). Owner/viewer are gone — see
// MEMORY/agents_teams_handoff_architecture.md.
export type TenantRole = "admin" | "user";

export interface Membership {
  tenantId: string;
  slug: string;
  name: string;
  role: TenantRole;
  enabledModules: string[];
}

// The nav's own VisibleSections map lived here: one boolean per surface,
// derived by branching on the platform and tenant role. It is gone, along with
// NONE_VISIBLE, CHAT_SECTIONS, maskChat, computeVisibleSections and
// canWriteInTenant. Nothing imported any of them: Shell.tsx filters its nav on
// the permission each leaf carries, and the server decides access on the
// permission each route carries. A second model of who-sees-what, kept in step
// by hand, is how the Audit Log ended up unreachable for the role whose job is
// to read it.
//
// shared/visible-sections.ts still holds the permission-driven version, which
// script/verify-rbac.ts uses to check the two agree against a live database.

/**
 * Resolve the role the current user holds in the currently-selected
 * tenant. Returns null when no tenant is selected or the user has no
 * membership (platform staff typically operate unscoped).
 */
export function resolveTenantRole(
  memberships: Membership[],
  activeSlug: string | null,
): TenantRole | null {
  if (!activeSlug) return null;
  const m = memberships.find((x) => x.slug === activeSlug);
  return m ? m.role : null;
}

/**
 * Resolve the enabled modules for the currently-selected tenant.
 * Returns an empty array when no tenant is selected.
 */
export function resolveEnabledModules(
  memberships: Membership[],
  activeSlug: string | null,
): string[] {
  if (!activeSlug) return [];
  const m = memberships.find((x) => x.slug === activeSlug);
  return m ? m.enabledModules : [];
}

// Impersonation removed Apr 2026 — log in as the actual user instead.
// (Stale localStorage entries under chloe-ops:impersonate-role are
// ignored; nothing reads them.)


// ── THE ACTIVE SCOPE'S PERMISSIONS ──────────────────────────────────────────
//
// READ THIS BEFORE GATING A CONTROL ON A PERMISSION.
//
// `me.permissions` is NOT the user's permissions. The server builds it as
// `loadEffectivePermissions(user, activeTenantId)` (server/ops/index.ts), where
// activeTenantId comes from the X-Chloe-Tenant-Slug header — which opsFetch only
// sends when a slug is in localStorage. So the top-level set silently contains
// no tenant-scoped permissions on any request where that slug was absent: a
// fresh login, after a logout, a new browser, a member who has never touched a
// switcher they have no reason to open.
//
// Every membership carries its own complete set, correct regardless of headers.
// That is the one to read, and this is the only function that should read it.
//
// WHAT IT COST. An Account Manager holding tenant_settings.manage, users.manage,
// billing.manage and workflow.manage opened Settings -> Users and found no
// "Add user" control at all — on the page whose entire purpose is managing
// users, on a workspace they administer. The server would have accepted the
// invite (users.manage is exactly what it asks for); only the screen refused.
// Billing and Workflow were gated the same wrong way, so three features were
// withheld from the person entitled to all three, with no error to search for.
// The same trap is documented at VoiceAgentStates.tsx:527, where it had already
// told a tenant admin "Supervisors only" on a page they had used all day.
//
// The fallback to the sole membership matters as much as the slug lookup: with
// one workspace, its permissions apply whether or not anything was ever stored.
interface PermissionHolder {
  // Declared structurally rather than importing `Me` from App.tsx, which
  // imports this module — the cycle is the reason this is not typed directly.
  platformRole?: PlatformRole | null;
  role?: PlatformRole | null;
  permissions?: string[] | null;
  memberships?: Array<{ slug: string; permissions?: string[] | null }> | null;
}

/** The effective permission set for the workspace currently in scope.
 *
 *  Prefer the MEMBERSHIP's set; fall back to the header-driven top-level one for
 *  the superadmin, who has no memberships at all. Every surface that gates a
 *  control on a permission resolves it here, so the next page cannot repeat the
 *  mistake described above. */
export function activePermissions(
  me: PermissionHolder,
  activeSlug: string | null,
): Set<string> {
  const ms = me.memberships ?? [];
  const m =
    ms.find((x) => x.slug === activeSlug) ?? (ms.length === 1 ? ms[0] : undefined);
  return new Set(m?.permissions ?? me.permissions ?? []);
}

/**
 * Does the user hold `key` in the workspace currently in scope?
 *
 * The platform owner passes everything, mirroring the server's superadmin
 * bypass in loadEffectivePermissions — a screen that refuses an owner the
 * server would admit is the same defect as the one above, pointed the other way.
 */
export function hasPermission(
  me: PermissionHolder,
  key: string,
  activeSlug: string | null,
): boolean {
  if ((me.platformRole ?? me.role) === "owner") return true;
  return activePermissions(me, activeSlug).has(key);
}

/**
 * Is this GoZupees staff? Platform Admin (role 1, `owner`) or Platform Staff
 * (role 2, `admin` — the forward-deployed engineer embedded with a client).
 *
 * This is NOT `canWriteInTenant`. A Tenant Admin (role 3) is the CLIENT's own
 * business owner: `platformRole === 'viewer'` with `tenant_users.role='admin'`.
 * They can write plenty inside their tenant — but they must never see or touch
 * the voice ENGINE (which provider runs an agent, its Dograh/Retell id). To the
 * client the engine is "VersaTalk"; the vendor beneath it is ours.
 *
 * The server-side mirror of this used to be requirePlatformStaff(); that gate is
 * gone and the engine routes now ask for voice.admin, which our staff hold and no
 * customer role does. The server is the real gate; this only decides what to
 * render. Reading the platform role here is therefore the ROUGH answer: it is
 * right for the seeded roles and would refuse a custom role granted voice.admin.
 * Moving it onto the permission is the remaining half of the two-sources-of-truth
 * cleanup (docs/rbac-and-admin-console-plan.md, Open items).
 * See docs/specs/voice-provider-agnostic-metrics.md §6.
 */
export function isPlatformStaff(platformRole: PlatformRole): boolean {
  return platformRole === "owner" || platformRole === "admin";
}
